/**
 * Config tab (#2575) — every knob with meaning, unit, and the same
 * range the platform route enforces. Out-of-range values are refused
 * here so a 400 is not how the operator first hears the window.
 */
import { useEffect, useState } from 'react';
import type {
  CodexCapacityAlertSettings,
  UpdateCodexCapacityAlertSettingsRequest,
} from '@nadoutong/contracts';
import {
  CODEX_CAPACITY_AUDIENCE_DAYS_MAX,
  CODEX_CAPACITY_AUDIENCE_DAYS_MIN,
  CODEX_CAPACITY_CLEAR_PCT_MIN,
  CODEX_CAPACITY_FIRE_PCT_MAX,
  CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE,
  CODEX_CAPACITY_RETENTION_HOURS_MIN,
  CODEX_CAPACITY_WINDOW_MINUTES_MIN,
} from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { Button, FormField, Input } from '../../ui/index.ts';
import {
  parseSettingsDraft,
  settingsToDraft,
  type CodexCapacitySettingsDraft,
} from './codexCapacityStatus.ts';

interface Props {
  readonly settings: CodexCapacityAlertSettings;
  readonly saving: boolean;
  readonly onSave: (body: UpdateCodexCapacityAlertSettingsRequest) => void;
}

type FieldKey = keyof CodexCapacitySettingsDraft;

export function CodexCapacitySettingsForm({ settings, saving, onSave }: Props): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState<CodexCapacitySettingsDraft>(() => settingsToDraft(settings));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(settingsToDraft(settings));
  }, [settings]);

  const setField = (field: FieldKey, value: string): void => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const submit = (): void => {
    const parsed = parseSettingsDraft(draft, settings);
    if (!parsed.ok) {
      setError(t(parsed.issue.key, parsed.issue.params));
      return;
    }
    setError(null);
    onSave(parsed.body);
  };

  const range = {
    fireMin: String(CODEX_CAPACITY_FIRE_PCT_MIN_EXCLUSIVE),
    fireMax: String(CODEX_CAPACITY_FIRE_PCT_MAX),
    clearMin: String(CODEX_CAPACITY_CLEAR_PCT_MIN),
    windowMin: String(CODEX_CAPACITY_WINDOW_MINUTES_MIN),
    retentionMin: String(CODEX_CAPACITY_RETENTION_HOURS_MIN),
    audienceMin: String(CODEX_CAPACITY_AUDIENCE_DAYS_MIN),
    audienceMax: String(CODEX_CAPACITY_AUDIENCE_DAYS_MAX),
  };

  return (
    <form
      className="flex flex-col gap-4"
      data-testid="codex-capacity-settings-form"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <NumberField
          fieldId="codex-capacity-fire-pct"
          label={t('codex_capacity.settings.fire_pct.label')}
          hint={t('codex_capacity.settings.fire_pct.hint', { min: range.fireMin, max: range.fireMax })}
          value={draft.fire_pct}
          onChange={(value) => setField('fire_pct', value)}
        />
        <NumberField
          fieldId="codex-capacity-clear-pct"
          label={t('codex_capacity.settings.clear_pct.label')}
          hint={t('codex_capacity.settings.clear_pct.hint', { min: range.clearMin })}
          value={draft.clear_pct}
          onChange={(value) => setField('clear_pct', value)}
        />
        <NumberField
          fieldId="codex-capacity-window-minutes"
          label={t('codex_capacity.settings.window_minutes.label')}
          hint={t('codex_capacity.settings.window_minutes.hint', { min: range.windowMin })}
          value={draft.window_minutes}
          onChange={(value) => setField('window_minutes', value)}
        />
        <NumberField
          fieldId="codex-capacity-clear-window-minutes"
          label={t('codex_capacity.settings.clear_window_minutes.label')}
          hint={t('codex_capacity.settings.clear_window_minutes.hint', { min: range.windowMin })}
          value={draft.clear_window_minutes}
          onChange={(value) => setField('clear_window_minutes', value)}
        />
        <NumberField
          fieldId="codex-capacity-min-window-requests"
          label={t('codex_capacity.settings.min_window_requests.label')}
          hint={t('codex_capacity.settings.min_window_requests.hint')}
          value={draft.min_window_requests}
          onChange={(value) => setField('min_window_requests', value)}
        />
        <NumberField
          fieldId="codex-capacity-audience-days"
          label={t('codex_capacity.settings.audience_days.label')}
          hint={t('codex_capacity.settings.audience_days.hint', {
            min: range.audienceMin,
            max: range.audienceMax,
          })}
          value={draft.audience_days}
          onChange={(value) => setField('audience_days', value)}
        />
        <NumberField
          fieldId="codex-capacity-max-sends"
          label={t('codex_capacity.settings.max_sends_per_pass.label')}
          hint={t('codex_capacity.settings.max_sends_per_pass.hint')}
          value={draft.max_sends_per_pass}
          onChange={(value) => setField('max_sends_per_pass', value)}
        />
        <NumberField
          fieldId="codex-capacity-cooldown-hours"
          label={t('codex_capacity.settings.cooldown_hours.label')}
          hint={t('codex_capacity.settings.cooldown_hours.hint')}
          value={draft.cooldown_hours}
          onChange={(value) => setField('cooldown_hours', value)}
        />
        <NumberField
          fieldId="codex-capacity-quiet-start"
          label={t('codex_capacity.settings.quiet_start.label')}
          hint={t('codex_capacity.settings.quiet_start.hint')}
          value={draft.quiet_hours_start_hour}
          onChange={(value) => setField('quiet_hours_start_hour', value)}
        />
        <NumberField
          fieldId="codex-capacity-quiet-end"
          label={t('codex_capacity.settings.quiet_end.label')}
          hint={t('codex_capacity.settings.quiet_end.hint')}
          value={draft.quiet_hours_end_hour}
          onChange={(value) => setField('quiet_hours_end_hour', value)}
        />
        <FormField
          label={t('codex_capacity.settings.quiet_tz.label')}
          fieldId="codex-capacity-quiet-tz"
          hint={t('codex_capacity.settings.quiet_tz.hint')}
        >
          <Input
            id="codex-capacity-quiet-tz"
            value={draft.quiet_hours_tz}
            onChange={(event) => setField('quiet_hours_tz', event.currentTarget.value)}
            data-testid="codex-capacity-quiet-tz"
          />
        </FormField>
        <NumberField
          fieldId="codex-capacity-retention-hours"
          label={t('codex_capacity.settings.retention_hours.label')}
          hint={t('codex_capacity.settings.retention_hours.hint', { min: range.retentionMin })}
          value={draft.retention_hours}
          onChange={(value) => setField('retention_hours', value)}
        />
        <NumberField
          fieldId="codex-capacity-rollup-lookback"
          label={t('codex_capacity.settings.rollup_lookback.label')}
          hint={t('codex_capacity.settings.rollup_lookback.hint', { min: range.windowMin })}
          value={draft.rollup_lookback_minutes}
          onChange={(value) => setField('rollup_lookback_minutes', value)}
        />
      </div>

      <FormField
        label={t('codex_capacity.settings.reason_label')}
        fieldId="codex-capacity-settings-reason"
        hint={t('codex_capacity.settings.reason_hint')}
      >
        <Input
          id="codex-capacity-settings-reason"
          value={draft.reason}
          onChange={(event) => setField('reason', event.currentTarget.value)}
          data-testid="codex-capacity-settings-reason"
        />
      </FormField>

      {error !== null ? (
        <p className="m-0 text-dense text-bad" role="alert" data-testid="codex-capacity-settings-error">
          {error}
        </p>
      ) : null}

      <div>
        <Button type="submit" loading={saving} data-testid="codex-capacity-settings-save">
          {t('codex_capacity.settings.save')}
        </Button>
      </div>
    </form>
  );
}

function NumberField({
  fieldId,
  label,
  hint,
  value,
  onChange,
}: {
  readonly fieldId: string;
  readonly label: string;
  readonly hint: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
}): JSX.Element {
  return (
    <FormField label={label} fieldId={fieldId} hint={hint}>
      <Input
        id={fieldId}
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        data-testid={fieldId}
      />
    </FormField>
  );
}
